/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { forwardRef } from "react";

import { colors, spacing, spacingMasks } from "../../../../../global/emotion";
import { useThemeValues } from "../../../../hooks/useThemeValues";
import { emotionDynamics } from "../../../../lib/emotionDynamics";
import type { Color } from "../../../../types/color.emotion.types";
import type { GradientDirection } from "../../../../types/gradientDirection.types";
import type { HallowElement } from "../../../../types/hallowElement.types";
import type { HallowElementProps } from "../../../../types/hallowElementProps.types";
import type { WithAsChild } from "../../../../types/withAsChild.types";
import type { CSSColorStrict } from "../../../../utils/colors/types/CSSColorStrict.types";
import { determineElementFromAsChild } from "../../../../utils/determineElementFromAsChild";

const hallowElement: HallowElement = "div";

const backgroundStyle: Interpolation<CSSColorStrict> = (color) => ({
  background: color,
});

const orientationStyles: Record<MaskProps["orientation"], Interpolation> = {
  horizontal: {
    width: spacing.full,
  },
  vertical: {
    height: spacing.full,
  },
};

const stylesHorizontalSize: Record<MaskProps["size"], Interpolation> = {
  xxs: {
    height: spacingMasks.xxs,
  },
  xs: {
    height: spacingMasks.xs,
  },
  s: {
    height: spacingMasks.s,
  },
  m: {
    height: spacingMasks.m,
  },
  l: {
    height: spacingMasks.l,
  },
  xl: {
    height: spacingMasks.xl,
  },
};

const stylesVerticalSize: Record<MaskProps["size"], Interpolation> = {
  xxs: {
    width: spacingMasks.xxs,
  },
  xs: {
    width: spacingMasks.xs,
  },
  s: {
    width: spacingMasks.s,
  },
  m: {
    width: spacingMasks.m,
  },
  l: {
    width: spacingMasks.l,
  },
  xl: {
    width: spacingMasks.xl,
  },
};

export type MaskProps = WithAsChild<
  HallowElementProps<typeof hallowElement>
> & {
  color?: Color;
  direction?: GradientDirection;
  orientation?: "horizontal" | "vertical";
  size?: keyof typeof spacingMasks;
};

export const Mask = forwardRef<any, MaskProps>(
  (
    {
      asChild = false,
      className,
      color = "background",
      direction = "to bottom",
      orientation = "horizontal",
      size = "m",
      ...props
    },
    ref,
  ) => {
    const DeterminedElement = determineElementFromAsChild({
      asChild,
      hallowElement,
    });
    const theme = useThemeValues();

    return (
      <DeterminedElement
        ref={ref}
        className={className}
        css={[
          orientationStyles[orientation],
          orientation === "horizontal"
            ? stylesHorizontalSize[size]
            : stylesVerticalSize[size],
          backgroundStyle(
            theme.semanticColors[color] ?? colors[color] ?? color,
          ),
          emotionDynamics.fullMask(direction),
        ]}
        {...props}
      />
    );
  },
);

Mask.displayName = "Mask";
